<template>
  <section>
    <h2 class="step-title-optional">
      {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@MISSION_TITLE")) }}
    </h2>

    <ExtraTipbox v-if="!hasShifts" class="q-my-sm font-090">
      <span>{{ $t("ETAB.PAGES.DEMANDS.CREATION.@MISSION_HINT") }}</span>
    </ExtraTipbox>
    <div class="creation-demand-step-shifts">
      <QList>
        <QItem class="row justify-between items-center">
          <span class="text-bold text-h4">{{ $t("ETAB.PAGES.DEMANDS.CREATION.@PLANNING_DATES_SUBSTEP") }}</span>
          <div class="field-planning">
            <Dates :step-data="stepData" :planning-days="planning.getDays()" @save-step="setupDates" />
          </div>
        </QItem>
        <QSeparator v-if="hasShifts" spaced inset />
        <QItem v-if="hasShifts && planning.getDays().length > 0" class="justify-between items-center" :class="$q.screen.lt.md ? 'column' : 'row'">
          <span class="text-bold text-h4">{{ $t("ETAB.PAGES.DEMANDS.CREATION.@TYPICAL_SCHEDULE_TITLE") }}</span>
          <TypicalSchedule v-model:typical-schedule="typicalSchedule" :first-shift="firstShift" @set-schedule="setSchedule" />
        </QItem>
        <QSeparator v-if="hasShifts && planning.getDays().length > 1" spaced inset />
        <QItem v-if="hasShifts" class="column">
          <span class="text-bold text-h4 text-left">
            {{ $t("ETAB.PAGES.DEMANDS.CREATION.@SHIFTS_TITLE") }}
          </span>
          <PlanningFull v-if="hasShiftsAndSchedule" v-model:planning="planning" :loading="loading" @save-step="save" @update-dates="setupDates" />
          <template v-else>
            <div class="row justify-between q-my-md shift-skeleton">
              <section class="column justify-around items-center">
                <q-skeleton type="text" width="60px" animation="fade" />
                <q-skeleton type="text" width="30px" animation="fade" />
              </section>
              <q-skeleton height="40px" width="70%" animation="fade" />
            </div>

            <p class="text-caption text-center">{{ $t("ETAB.PAGES.DEMANDS.CREATION.@SHIFTS_HINT") }}</p>
          </template>
        </QItem>
      </QList>
    </div>
  </section>
</template>

<script>
  import { CreationStepPlanningModel, ICreationStepPlanningDay, PlanningDayModel } from "@core/models";

  import Dates from "@etab/components/creation/DatesShiftsAE.component.vue";
  import TypicalSchedule from "@etab/components/creation/TypicalSchedule.component.vue";
  import PlanningFull from "@etab/components/creation/PlanningFull.component.vue";

  import { DatesHelpers } from "@core/helpers";
  import { API_DATE_FORMAT_SHORT } from "@core/constants";

  let delay;
  export default {
    components: {
      Dates,
      TypicalSchedule,
      PlanningFull,
    },
    props: {
      stepData: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
      stepAssets: {
        type: Object,
        default: () => ({}),
      },
    },
    emits: ["saveStep"],
    data() {
      return {
        planning: {
          type: Object,
          default: new CreationStepPlanningModel(),
        },
        typicalSchedule: {
          [ICreationStepPlanningDay.start]: null,
          [ICreationStepPlanningDay.end]: null,
          [ICreationStepPlanningDay.break]: 0, // force break value to 0
        },
      };
    },
    computed: {
      hasShifts() {
        return this.planning.getDays().length;
      },
      firstShift() {
        return this.hasShifts ? this.planning.getDays()[0] : null;
      },
      hasShiftsAndSchedule() {
        if (this.hasShifts && this.firstShift) {
          return (
            !Number.isNaN(this.firstShift[ICreationStepPlanningDay.start]) && // can be zero
            !!this.firstShift[ICreationStepPlanningDay.end] &&
            !Number.isNaN(this.firstShift[ICreationStepPlanningDay.break]) // can be zero
          );
        }
        return false;
      },
    },
    beforeUnmount() {
      clearTimeout(delay);
    },
    created() {
      // init a planning model which has both a dates model and a schedule model
      // init step
      this.planning = new CreationStepPlanningModel(this.stepData);
      this.initTypicalSchedule();
    },
    methods: {
      initTypicalSchedule() {
        const firstShift = this.hasShifts ? this.planning.getDays()[0] : null;
        if (firstShift) {
          //Retrieve typical schedule from first shift
          this.typicalSchedule = {
            [ICreationStepPlanningDay.start]: firstShift[ICreationStepPlanningDay.start],
            [ICreationStepPlanningDay.end]: firstShift[ICreationStepPlanningDay.end],
            [ICreationStepPlanningDay.break]: firstShift[ICreationStepPlanningDay.break],
          };
        }
      },
      setupDates(data) {
        const { start, end } = data.dates;
        this.planning.setDates({ start, end });
        this.planning.initFromDate({ start, end, defaultSchedule: this.typicalSchedule, previousData: [...this.planning.getDays()] });
      },
      setSchedule({ property, value }) {
        this.typicalSchedule[property] = value;
        // we set for every activated days
        this.planning.days
          .filter((d) => d.selected)
          .forEach((d) => {
            this.planning.scheduleDay({ dayId: d.id, property, value });
          });
      },
      save() {
        const days = [];
        this.planning.getDays().forEach((day) => {
          if (day[ICreationStepPlanningDay.selected]) {
            const dayDTO = { ...day };
            delete dayDTO[ICreationStepPlanningDay.id];
            delete dayDTO[ICreationStepPlanningDay.selected];
            dayDTO[ICreationStepPlanningDay.date] = DatesHelpers.getDayjs(dayDTO[ICreationStepPlanningDay.date]).format(API_DATE_FORMAT_SHORT);
            days.push(dayDTO);
          }
        });
        const shifts = {
          days,
        };
        this.$emit("saveStep", shifts);
      },
    },
  };
</script>

<style lang="scss">
  .creation-demand-step-shifts {
    width: 100%;
    background: white;
    border-radius: $button-border-radius;
    box-shadow: $card-shadow;
    padding-bottom: 10px;
    margin-bottom: 80px;
    &__stepper {
      box-shadow: none;
    }
    .shift-skeleton {
      > section {
        flex: 1;
      }
    }
    .field-planning {
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
